import { Injectable, computed, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { AsyncTask } from './async-task.service';

/** Parsed 'medialabel.ai-configuration' tenant parameter (Java V091). */
export interface MediaLabelConfiguration {
  autoOnUpload: boolean;
  types: { images: boolean; video: boolean; pdf: boolean; widgets: boolean };
  allowNewLabelProposals: boolean;
  maxNewLabelsPerGroup: number;
  confidenceThreshold: number;
  /** Jobs per calendar day, all sources; 0 = unlimited. */
  dailyJobLimit: number;
}

export interface AutolabelExistingMatch {
  labelUuid: string;
  name: string;
  confidence: number;
  rationale?: string;
  applied: boolean;
  /** Auto-apply chose it but could not attach it (run without an owner): a decision is owed. */
  needsReview?: boolean;
  /** Declined by the reviewer (apply with rejectRest). */
  rejected?: boolean;
}

export interface AutolabelProposal {
  name: string;
  confidence: number;
  rationale?: string;
  accepted: boolean;
  rejected: boolean;
  labelUuid?: string | null;
}

export interface AutolabelGroup {
  definitionUuid: string;
  definitionName: string;
  existing: AutolabelExistingMatch[];
  proposed: AutolabelProposal[];
}

export interface AutolabelResult {
  summary: string;
  groups: AutolabelGroup[];
}

export interface MediaAssetLabelSuggestion {
  uuid: string;
  jobUuid: string;
  batchUuid: string;
  mediaassetUuid: string;
  source: 'UPLOAD' | 'MANUAL';
  status: 'PENDING' | 'DONE' | 'FAILED';
  summary: string | null;
  suggestions: AutolabelResult | null;
  appliedLabelUuids: string[];
  pendingProposals: number;
  resolution: 'ACCEPTED' | 'REJECTED' | 'PARTIAL' | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface AnalyzeResponse {
  /** Null when nothing was started (dry run, no candidates). */
  batchUuid: string | null;
  /**
   * deduped: an analysis of that asset was already running and is reused; batchUuid is then the
   * earlier batch that owns it (e.g. the upload trigger), otherwise this response's batchUuid.
   */
  jobs: { jobId: string; mediaassetUuid: string; deduped: boolean; batchUuid: string }[];
  skipped: { mediaassetUuid: string; reason: 'notFound' | 'unsupportedType' | 'dailyLimit' }[];
  /** Library-wide runs: assets matching the filter before the run limit and daily cap. */
  candidates?: number;
}

export interface AnalyzeAllRequest {
  /** image | video | pdf | widget; empty = every family enabled for the tenant. */
  types?: string[];
  unlabeledOnly?: boolean;
  dryRun?: boolean;
  limit?: number;
}

export interface ApplySuggestionRequest {
  acceptExistingLabelUuids: string[];
  acceptProposals: { definitionUuid: string; name: string }[];
  rejectRest: boolean;
}

/** Aggregate batch result carried in AsyncTask.result for type 'media-autolabel'. */
export interface AutolabelBatchResult {
  total: number;
  done: number;
  failed: number;
  applied: number;
  pendingProposals: number;
}

const DEFAULT_CONFIGURATION: MediaLabelConfiguration = {
  autoOnUpload: false,
  types: { images: true, video: true, pdf: true, widgets: false },
  allowNewLabelProposals: true,
  maxNewLabelsPerGroup: 4,
  confidenceThreshold: 0.6,
  dailyJobLimit: 500,
};

/**
 * AI auto-labeling of media assets (`/api/core/medialabel/*`). Analysis runs as a batch job on the
 * backend; progress arrives through AsyncTaskService (type 'media-autolabel', task id = batchUuid).
 * Existing labels above the threshold are attached by the backend; proposed NEW labels wait in a
 * suggestion until the user applies or rejects them here.
 */
@Injectable({ providedIn: 'root' })
export class MediaLabelAiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = '/api/core/medialabel';

  static readonly TASK_TYPE = 'media-autolabel';

  private readonly configuration$: Observable<MediaLabelConfiguration> = this.http
    .get<MediaLabelConfiguration>('/api/core/parameter/medialabel-configuration')
    .pipe(
      map((config) => ({
        ...DEFAULT_CONFIGURATION,
        ...config,
        types: { ...DEFAULT_CONFIGURATION.types, ...config?.types },
      })),
      catchError(() => of(DEFAULT_CONFIGURATION)),
      shareReplay(1),
    );

  readonly configuration = toSignal(this.configuration$, { initialValue: DEFAULT_CONFIGURATION });

  /** At least one media type family is enabled — gates the library-wide action. */
  readonly anyTypeEnabled = computed(() => Object.values(this.configuration().types).some(Boolean));

  /** Same type mapping as the Java MediaLabelConfigurationService (incl. the 'personal_video' pseudo-type). */
  isEnabledFor(type: string | null | undefined): boolean {
    if (!type) return false;
    const types = this.configuration().types;
    switch (type) {
      case 'image':
      case 'gif':
        return types.images;
      case 'video':
      case 'personal_video':
        return types.video;
      case 'document':
      case 'pdf':
        return types.pdf;
      case 'widget':
        return types.widgets;
      default:
        return false;
    }
  }

  analyze(mediaassetUuids: string[]): Observable<AnalyzeResponse> {
    return this.http.post<AnalyzeResponse>(`${this.baseUrl}/analyze`, { mediaassetUuids });
  }

  /** Library-wide run; `dryRun` only returns `candidates`. */
  analyzeAll(request: AnalyzeAllRequest): Observable<AnalyzeResponse> {
    return this.http.post<AnalyzeResponse>(`${this.baseUrl}/analyze-all`, request);
  }

  /**
   * True when a finished upload starts an AI labeling run by itself (`autoOnUpload` and at least one
   * enabled media type). Reads the loaded configuration, not the signal, so it is right even when the
   * upload finishes before the signal's first value.
   */
  autoLabelsOnUpload$(): Observable<boolean> {
    return this.configuration$.pipe(map((config) => config.autoOnUpload && Object.values(config.types).some(Boolean)));
  }

  /**
   * Batch of the labeling run still in flight for an asset — the upload trigger starts one that nobody
   * subscribed to, so the panel asks here after an upload and follows its stream. `null` = no run.
   */
  activeBatchOf(mediaassetUuid: string): Observable<string | null> {
    return this.http
      .get<{ batchUuid: string }>(`${this.baseUrl}/assets/${mediaassetUuid}/active-batch`, { observe: 'response' })
      .pipe(
        map((response) => response.body?.batchUuid ?? null),
        catchError(() => of(null)),
      );
  }

  batchStreamUrl(batchUuid: string): string {
    return `${this.baseUrl}/batches/${batchUuid}/stream`;
  }

  batchStatusUrl(batchUuid: string): string {
    return `${this.baseUrl}/batches/${batchUuid}`;
  }

  getBatchState(batchUuid: string): Observable<AsyncTask> {
    return this.http.get<AsyncTask>(this.batchStatusUrl(batchUuid));
  }

  getBatchSuggestions(batchUuid: string): Observable<MediaAssetLabelSuggestion[]> {
    return this.http.get<MediaAssetLabelSuggestion[]>(`${this.baseUrl}/batches/${batchUuid}/suggestions`);
  }

  getSuggestion(uuid: string): Observable<MediaAssetLabelSuggestion> {
    return this.http.get<MediaAssetLabelSuggestion>(`${this.baseUrl}/suggestions/${uuid}`);
  }

  search(params: { mediaassetUuid?: string; pendingOnly?: boolean; page?: number; size?: number }): Observable<{ content: MediaAssetLabelSuggestion[]; totalElements: number }> {
    let httpParams = new HttpParams();
    if (params.mediaassetUuid) httpParams = httpParams.set('mediaassetUuid', params.mediaassetUuid);
    if (params.pendingOnly !== undefined) httpParams = httpParams.set('pendingOnly', String(params.pendingOnly));
    if (params.page !== undefined) httpParams = httpParams.set('page', String(params.page));
    if (params.size !== undefined) httpParams = httpParams.set('size', String(params.size));
    return this.http.get<{ content: MediaAssetLabelSuggestion[]; totalElements: number }>(`${this.baseUrl}/suggestions`, { params: httpParams });
  }

  /** mediaasset uuid → proposals awaiting a decision; assets without pending proposals are absent. */
  pendingSummary(mediaassetUuids: string[]): Observable<Record<string, number>> {
    if (!mediaassetUuids.length) return of({});
    return this.http.post<Record<string, number>>(`${this.baseUrl}/pending-summary`, { mediaassetUuids }).pipe(catchError(() => of({})));
  }

  apply(suggestionUuid: string, request: ApplySuggestionRequest): Observable<MediaAssetLabelSuggestion> {
    return this.http.post<MediaAssetLabelSuggestion>(`${this.baseUrl}/suggestions/${suggestionUuid}/apply`, request);
  }
}
